import { Play, Pause, Square, Circle, SkipBack, SkipForward, Repeat, Music, Activity } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useStudioStore } from '@/store/useStudioStore';

interface TransportControlsProps {
  isMobile?: boolean;
  isTablet?: boolean;
}

export function TransportControls({ isMobile = false, isTablet = false }: TransportControlsProps) {
  const isPlaying = useStudioStore((state) => state.isPlaying);
  const bpm = useStudioStore((state) => state.bpm);
  const currentStep = useStudioStore((state) => state.currentStep);
  const togglePlay = useStudioStore((state) => state.togglePlay);
  const stop = useStudioStore((state) => state.stop);
  const setBpm = useStudioStore((state) => state.setBpm);
  
  const [isRecording, setIsRecording] = useState(false);
  const [cpuUsage] = useState(35); // Mock CPU usage for display
  const [timeSignature, setTimeSignature] = useState('4/4');
  const [loopEnabled, setLoopEnabled] = useState(false);

  // Calculate position from currentStep
  const position = `1.${Math.floor(currentStep / 4) + 1}.${(currentStep % 4) * 24}`;

  // Mock playback loop - increments currentStep when playing
  useEffect(() => {
    if (!isPlaying) return;

    // Calculate interval based on BPM (quarter note timing)
    // 60 seconds / BPM = seconds per beat
    // Divide by 4 for 16th note resolution (16 steps per 4 beats)
    const intervalMs = (60 / bpm) * 1000 / 4;

    const interval = setInterval(() => {
      useStudioStore.setState((state) => ({
        currentStep: (state.currentStep + 1) % 16, // Loop back to 0 after step 15
      }));
    }, intervalMs);

    return () => clearInterval(interval);
  }, [isPlaying, bpm]);

  return (
    <div className={`${isMobile ? 'h-16' : 'h-20'} bg-[#1f1f1f] border-t border-[#3a3a3a] flex items-center justify-between px-2 md:px-4 overflow-x-auto`}>
      {/* Transport buttons */}
      <div className="flex items-center gap-1 md:gap-2">
        {/* Play/Pause Toggle Button */}
        <button
          onClick={togglePlay}
          className={`${isMobile ? 'p-2' : 'p-4'} rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95 ${
            isPlaying 
              ? 'bg-[#ff6b00] shadow-lg shadow-[#ff6b00]/50 hover:bg-[#ff8b30]' 
              : 'bg-[#3a3a3a] hover:bg-[#4a4a4a] hover:shadow-md'
          }`}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause size={isMobile ? 18 : 24} fill="currentColor" /> : <Play size={isMobile ? 18 : 24} fill="currentColor" />}
        </button>
        
        {/* Stop Button */}
        <button
          onClick={stop}
          className={`${isMobile ? 'p-2' : 'p-4'} rounded-lg bg-[#3a3a3a] hover:bg-[#4a4a4a] hover:shadow-md transition-all duration-200 transform hover:scale-105 active:scale-95`}
          title="Stop"
        >
          <Square size={isMobile ? 18 : 24} fill="currentColor" />
        </button>
        
        {!isMobile && <div className="w-px h-12 bg-[#3a3a3a] mx-2" />}
        
        {/* Record Button - Hidden on mobile */}
        {!isMobile && (
          <button
            onClick={() => {
              setIsRecording(!isRecording);
              if (!isPlaying) togglePlay();
            }}
            className={`p-4 rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95 ${
              isRecording 
                ? 'bg-red-600 shadow-lg shadow-red-600/50 animate-pulse hover:bg-red-700' 
                : 'bg-[#3a3a3a] hover:bg-[#4a4a4a] hover:shadow-md'
            }`}
            title={isRecording ? 'Stop Recording' : 'Record'}
          >
            <Circle size={24} className={isRecording ? 'fill-current' : ''} />
          </button>
        )}
        
        {!isMobile && <div className="w-px h-12 bg-[#3a3a3a] mx-2" />}
        
        {/* Skip buttons - Hidden on mobile */}
        {!isMobile && (
          <>
            <button className="p-3 hover:bg-[#2a2a2a] rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95" title="Previous">
              <SkipBack size={20} />
            </button>
            
            <button className="p-3 hover:bg-[#2a2a2a] rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95" title="Next">
              <SkipForward size={20} />
            </button>
          </>
        )}

        <button
          onClick={() => setLoopEnabled(!loopEnabled)}
          className={`${isMobile ? 'p-2' : 'p-3'} rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95 ${
            loopEnabled ? 'bg-[#ff6b00] text-white shadow-md shadow-[#ff6b00]/30' : 'hover:bg-[#2a2a2a]'
          }`}
          title={loopEnabled ? 'Loop Enabled' : 'Loop Disabled'}
        >
          <Repeat size={isMobile ? 16 : 20} />
        </button>
      </div>

      {/* Position display - Responsive */}
      {!isMobile && (
        <div className="flex items-center gap-3 md:gap-6">
          <div className="bg-[#0f0f0f] px-2 md:px-4 py-1 md:py-2 rounded border border-[#3a3a3a]">
            <div className="text-[10px] md:text-xs opacity-50 mb-0.5">POSITION</div>
            <div className="font-mono text-base md:text-xl text-[#ff6b00]">{position}</div>
          </div>

          {!isTablet && (
            <div className="flex items-center gap-2 md:gap-3">
              <Music size={16} className="opacity-50" />
              <select
                value={timeSignature}
                onChange={(e) => setTimeSignature(e.target.value)}
                className="bg-[#2a2a2a] border border-[#3a3a3a] rounded px-2 md:px-3 py-1 md:py-2 text-xs md:text-sm"
              >
                <option>4/4</option>
                <option>3/4</option>
                <option>6/8</option>
                <option>7/8</option>
              </select>
            </div>
          )}
        </div>
      )}
      
      {/* BPM and stats - Responsive */}
      <div className="flex items-center gap-2 md:gap-6">
        {/* BPM Slider with Numeric Display */}
        <div className="flex items-center gap-2 md:gap-3">
          {!isMobile && <div className="text-[10px] md:text-xs opacity-70">TEMPO</div>}
          <div className="flex flex-col gap-1">
            {!isMobile && (
              <input
                type="range"
                value={bpm}
                onChange={(e) => setBpm(Number(e.target.value))}
                className="w-20 md:w-32 h-2 bg-[#2a2a2a] rounded-lg appearance-none cursor-pointer slider"
                min="60"
                max="200"
                step="1"
              />
            )}
            <input
              type="number"
              value={bpm}
              onChange={(e) => setBpm(Number(e.target.value))}
              className="w-16 md:w-20 bg-[#2a2a2a] border border-[#3a3a3a] rounded px-1 md:px-2 py-1 text-center font-mono text-xs md:text-sm"
              min="60"
              max="200"
            />
          </div>
          <span className="text-[10px] md:text-xs opacity-70">BPM</span>
        </div>
        
        {/* CPU meter - Hidden on mobile */}
        {!isMobile && (
          <div className="flex items-center gap-2 md:gap-3 bg-[#2a2a2a] px-2 md:px-3 py-1 md:py-2 rounded">
            <Activity size={14} className="text-[#ff6b00]" />
            <div className="flex flex-col">
              <span className="text-[9px] opacity-50">CPU</span>
              <div className="flex items-center gap-1 md:gap-2">
                <div className="w-16 md:w-24 h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 transition-all"
                    style={{ width: `${cpuUsage}%` }}
                  />
                </div>
                <span className="text-[10px] md:text-xs font-mono w-6 md:w-8">{cpuUsage}%</span>
              </div>
            </div>
          </div>
        )}

        {/* Polyphony - Hidden on mobile and tablet */}
        {!isMobile && !isTablet && (
          <div className="flex items-center gap-3 bg-[#2a2a2a] px-3 py-2 rounded">
            <div className="flex flex-col">
              <span className="text-[9px] opacity-50">POLYPHONY</span>
              <span className="text-xs font-mono">24/256</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}